import { BOSSES } from './bosses.js';
import { ACT2_BOSSES } from './act2Bosses.js';
import { ACT3_BOSSES } from './act3Bosses.js';
import { RadialRing } from '../systems/spawning/patterns/RadialRing.js';
import { HomingShardVolley } from '../systems/spawning/patterns/HomingShardVolley.js';
import { SweepingLaser } from '../systems/spawning/patterns/SweepingLaser.js';
import { BulletBoardGrid } from '../systems/spawning/patterns/BulletBoardGrid.js';
import { Implosion } from '../systems/spawning/patterns/Implosion.js';
import { RiptideVortex } from '../systems/spawning/patterns/RiptideVortex.js';
import { Undertow } from '../systems/spawning/patterns/Undertow.js';
import { PetrifyingGaze } from '../systems/spawning/patterns/PetrifyingGaze.js';
import { StutterField } from '../systems/spawning/patterns/StutterField.js';
import { TemporalRewind } from '../systems/spawning/patterns/TemporalRewind.js';
import { DejaVu } from '../systems/spawning/patterns/DejaVu.js';
import { Sirenreef } from '../systems/spawning/patterns/Sirenreef.js';
import { Palimpsest } from '../systems/spawning/patterns/Palimpsest.js';

/**
 * Challenge-exclusive bosses — reachable ONLY through Boss Challenge (see
 * ui/BossSelect.js + Game's challengeBossIndex). Deliberately not part of
 * BOSSES: the normal gauntlet, endless loops, heralds, Echo Duel, and the
 * leaderboard all continue to know nothing about them. Each one is built
 * around a mechanic the gauntlet doesn't have — free-standing currents
 * that push/pull without a leash point (Maelstrom Verge; the Jealous
 * Regent's Embrace is a leash TO the boss, a different dilemma), punishing
 * movement itself (The Unblinking), and re-tracing bullets backward along
 * flights already flown (The Chronophage; the Iron Cadence's Grand Pause
 * freezes and resumes, never reverses) — rather than a config retune of an
 * existing kit. Same def shape as BOSSES so the whole
 * Boss/SpawningSystem/renderer pipeline works unchanged; `subtitle` is
 * extra, read only by the Boss Challenge pick screen.
 */
export const CHALLENGE_BOSSES = [
  {
    name: 'The Sirenreef', subtitle: 'Exclusive · Lure',
    arenaTheme: { floor: '#101522', line: '#28445e', accent: '#ff6b9d' },
    spriteKey: 'sirenreef', musicTheme: 'sirenreef', maxHp: 10500, moveSpeed: 38,
    layerFromPhase: 1, pickCooldownMultiplier: 0.82,
    createPatterns: () => [
      new Sirenreef({ config: { count: 16, speed: 90, damage: 12, offset: 48 } }),
      new RadialRing({ minPhase: 0, config: { damage: 11, speed: 92 } }),
      new SweepingLaser({ minPhase: 1, config: { tickDamage: 6, tickInterval: 0.14, width: 7, length: 9999, sweepSpeed: 1.5 } }),
    ],
    waveConfig: { waveCount: 4, minionsPerWave: 12, minionHp: 58, minionMoveSpeed: 92, minionContactDamage: 12, minionShotDamage: 6, minionShotSpeed: 88, minionFireInterval: 1.8 },
  },
  {
    name: 'The Palimpsest', subtitle: 'Exclusive · Echo',
    arenaTheme: { floor: '#121020', line: '#3f3560', accent: '#d58cff' },
    spriteKey: 'palimpsest', musicTheme: 'palimpsest', maxHp: 12500, moveSpeed: 42,
    layerFromPhase: 1, pickCooldownMultiplier: 0.78,
    createPatterns: () => [
      new Palimpsest({ config: { interval: 0.72, count: 7, spread: 0.12, speed: 100, damage: 12 } }),
      new HomingShardVolley({ minPhase: 0, config: { damage: 12, speed: 100, turnRate: 1.8 } }),
      new TemporalRewind({ minPhase: 1, config: { ringCount: 12, ringSpeed: 76, ringDamage: 11 } }),
    ],
    waveConfig: { waveCount: 4, minionsPerWave: 12, minionHp: 62, minionMoveSpeed: 94, minionContactDamage: 13, minionShotDamage: 6, minionShotSpeed: 92, minionFireInterval: 1.75 },
  },
  {
    name: 'The Maelstrom Verge',
    subtitle: 'Exclusive · Tides',
    arenaTheme: { floor: '#0d1a1e', line: '#274d55', accent: '#4dd8c7' },
    spriteKey: 'maelstromVerge',
    musicTheme: 'maelstromVerge',

    maxHp: 6500,
    moveSpeed: 34,
    layerFromPhase: 2,
    pickCooldownMultiplier: 0.9,
    createPatterns: () => [
      new RiptideVortex({ config: { pullRadius: 150, pullStrength: 52, coreRadius: 16, coreDamage: 10, spitInterval: 0.34, bulletSpeed: 68, bulletDamage: 9 } }),
      new Undertow({ config: { pushStrength: 46, rankInterval: 0.5, rankSpacing: 26, gapSize: 52, bulletSpeed: 62, bulletDamage: 10 } }),
      new RadialRing({ minPhase: 1, config: { damage: 9, speed: 78 } }),
      new HomingShardVolley({ minPhase: 2, config: { damage: 11, speed: 95, turnRate: 1.6 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 11,
      minionHp: 46,
      minionMoveSpeed: 84,
      minionContactDamage: 10,
      minionShotDamage: 5,
      minionShotSpeed: 82,
      minionFireInterval: 2.0,
    },
  },
  {
    name: 'The Unblinking',
    subtitle: 'Exclusive · Stillness',
    arenaTheme: { floor: '#0f160f', line: '#31502c', accent: '#8ae06e' },
    spriteKey: 'theUnblinking',
    musicTheme: 'theUnblinking',

    maxHp: 7500,
    moveSpeed: 26, // a watcher, not a chaser — the gaze does the pressing
    layerFromPhase: 1, // the stand-still-vs-dodge dilemma IS the fight, so layering starts early
    pickCooldownMultiplier: 1.0,
    createPatterns: () => [
      new PetrifyingGaze({ config: { gazeDamage: 9, tearInterval: 0.85, tearSpeed: 58, tearDamage: 8 } }),
      new StutterField({ config: { count: 26, spraySpeed: 150, bulletDamage: 9, relaunchSpeed: 95 } }),
      new SweepingLaser({ minPhase: 1, config: { tickDamage: 6, tickInterval: 0.14, width: 7, length: 9999, sweepSpeed: 1.4 } }),
      new BulletBoardGrid({ minPhase: 2, config: { damage: 12, speed: 60 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 50,
      minionMoveSpeed: 88,
      minionContactDamage: 11,
      minionShotDamage: 5,
      minionShotSpeed: 86,
      minionFireInterval: 1.9,
    },
  },
  {
    name: 'The Chronophage',
    subtitle: 'Exclusive · Time',
    arenaTheme: { floor: '#12101f', line: '#3d3564', accent: '#8c7bff' },
    spriteKey: 'chronophage',
    musicTheme: 'chronophage',

    maxHp: 9500,
    moveSpeed: 44,
    layerFromPhase: 1,
    pickCooldownMultiplier: 0.75,
    createPatterns: () => [
      new TemporalRewind({ config: { ringCount: 14, ringSpeed: 72, ringDamage: 10 } }),
      new DejaVu({ config: { strikeInterval: 0.3, fuseTime: 0.6, strikeRadius: 15, strikeDamage: 12, shrapnelSpeed: 78, shrapnelDamage: 8 } }),
      new Implosion({ minPhase: 1, config: { count: 18, damage: 11, speed: 140 } }),
      new RadialRing({ minPhase: 2, config: { damage: 12, speed: 105 } }),
    ],
    waveConfig: {
      waveCount: 4,
      minionsPerWave: 12,
      minionHp: 54,
      minionMoveSpeed: 92,
      minionContactDamage: 12,
      minionShotDamage: 6,
      minionShotSpeed: 90,
      minionFireInterval: 1.8,
    },
  },
];

/**
 * The Boss Challenge pick order: the gauntlet bosses first (indices 0 to
 * BOSSES.length-1, matching BOSSES exactly — seven since the 2026-07
 * expansion), then the exclusives, then the Act II bosses. Game's
 * challengeBossIndex indexes into THIS list, so the exclusives are reachable
 * only via that path.
 *
 * ACT2_BOSSES and ACT3_BOSSES ride here for the same reason, and are kept in
 * their own arrays (not folded into CHALLENGE_BOSSES) so the exclusives stay
 * semantically pure: they are campaign bosses, and Boss Challenge is simply the
 * way to fight any one of them in isolation. That matters more for Act III than
 * it did for Act II — its bosses are the only ones in the game whose difficulty
 * is authored by the player's own build and movement history, which is not
 * something a single campaign run through them evaluates well.
 */
export const CHALLENGE_ROSTER = [...BOSSES, ...CHALLENGE_BOSSES, ...ACT2_BOSSES, ...ACT3_BOSSES];

/** Converts a boss's HP tier into a fair starting level for a solo challenge. */
export function challengeLevelForBoss(bossDef) {
  const hpRatio = Math.max(1, bossDef.maxHp / 3000);
  return Math.max(3, Math.min(18, Math.round(3 + Math.log2(hpRatio) * 2.5)));
}
